import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Users, 
  CheckCircle, 
  ArrowRight,
  BookOpen,
  Share2,
  Sparkles,
  Crown,
  Lightbulb,
  Bird
} from 'lucide-react';
import { 
  DiscordIcon, 
  QuestionIcon, 
  CodeIcon, 
  SnakeIcon, 
  TicTacIcon, 
  BookOffIcon, 
  TargetIcon, 
  UnrealEngineIcon, 
  GearIcon 
} from '../components/icons/SvgIcons';
import { EXTERNAL_LINKS } from '@/constants';
import SEO from '../components/SEO';

const MyFirstGame = () => {
  return (
    <div className="min-h-screen -mt-4">
      <SEO 
        title="Mon Premier Jeu - Apprends à coder tes jeux vidéo en pur code !"
        description="Formation de programmation de jeux vidéo en pur code, sans moteur, sans installation, sans compilation. Accessible aux débutants, aucun prérequis."
        keywords="programmation jeux vidéo, p5.js, JavaScript, tutorial, tuto, tutoriel, formation enfant, cours code, débutant, pas moteur, Pass Codeur, pur code, codeur, développement, jeu vidéo, mon premier jeu"
        url="/"
        type="website"
      />
      {/* Hero Section - Utilise les couleurs du Pass Codeur */}
      <section className="relative bg-gradient-to-br from-emerald-600 via-blue-600 to-violet-600 text-white overflow-hidden py-8 sm:py-12">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative w-full py-8 sm:py-16 text-center">
          <div className="max-w-5xl mx-auto px-6">
          <h1 className="text-5xl sm:text-6xl md:text-8xl font-bold mb-6 sm:mb-8 leading-tight">
              <span className="bg-gradient-to-r from-yellow-300 via-orange-300 to-pink-300 bg-clip-text text-transparent">
                Bienvenue sur
              </span><br />
              <span className="text-white">Mon Premier Jeu</span>
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl mb-6 sm:mb-8 text-emerald-100 leading-relaxed max-w-4xl mx-auto">
              Apprends à coder tes jeux vidéo en pur code,<br/>sans moteur, sans installation, sans compilation, directement depuis ce site.
            </p>
            <p className="text-base sm:text-lg mb-8 sm:mb-12 text-white font-medium">
              Cette formation est accessible aux débutants, à partir de 10 ans, aucun prérequis nécessaire !
            </p>
            
            <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 mb-8 sm:mb-12">
              <div className="bg-white/15 backdrop-blur-sm rounded-2xl p-4 sm:p-8 border border-white/20 hover:bg-white/20 transition-all">
                <Lightbulb className="w-12 sm:w-16 h-12 sm:h-16 text-yellow-300 mb-4 sm:mb-6 mx-auto" />
                <p className="text-lg sm:text-xl font-semibold"><strong>Donne vie à tes idées</strong></p>
                <p className="text-emerald-100 mt-2 text-base sm:text-lg">Découvre la programmation de jeux vidéos.</p>
              </div>
              <div className="bg-white/15 backdrop-blur-sm rounded-2xl p-4 sm:p-8 border border-white/20 hover:bg-white/20 transition-all">
                <div className="relative mx-auto w-12 sm:w-16 h-12 sm:h-16 mb-4 sm:mb-6">
                  <UnrealEngineIcon className="w-12 sm:w-16 h-12 sm:h-16 text-orange-300" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-20 sm:w-24 h-1 bg-red-300 rotate-45 transform"></div>
                  </div>
                </div>
                <p className="text-lg sm:text-xl font-semibold"><strong>Code en pur code, sans moteur complexe</strong></p>
                <p className="text-emerald-100 mt-2 text-base sm:text-lg">Concentre-toi uniquement sur le code.</p>
              </div>
              <div className="bg-white/15 backdrop-blur-sm rounded-2xl p-4 sm:p-8 border border-white/20 hover:bg-white/20 transition-all">
                <GearIcon className="w-12 sm:w-16 h-12 sm:h-16 text-pink-300 mb-4 sm:mb-6 mx-auto" />
                <p className="text-lg sm:text-xl font-semibold"><strong>Aucune installation / compilation</strong></p>
                <p className="text-emerald-100 mt-2 text-base sm:text-lg">Code directement et vois le résultat instantanément depuis ce site.</p>
              </div>
              <div className="bg-white/15 backdrop-blur-sm rounded-2xl p-4 sm:p-8 border border-white/20 hover:bg-white/20 transition-all">
                <BookOffIcon className="w-12 sm:w-16 h-12 sm:h-16 text-purple-300 mb-4 sm:mb-6 mx-auto" />
                <p className="text-lg sm:text-xl font-semibold"><strong>Aucun prérequis</strong></p>
                <p className="text-emerald-100 mt-2 text-base sm:text-lg">Crée ton premier jeu vidéo sans connaissance préalable.</p>
              </div>
            </div>
            <br/>
            {/* Bouton C'est parti */}
            <Button 
              size="lg" 
              className="bg-white text-black hover:bg-gray-100 text-lg sm:text-2xl px-12 sm:px-20 py-6 sm:py-10 rounded-full font-black shadow-2xl transform hover:scale-105 transition-all mb-8"
              asChild
            >
              <Link to="/courses">
                👉 C'EST PARTI ! <ArrowRight className="ml-2 sm:ml-4 w-6 sm:w-8 h-6 sm:h-8" />
              </Link>
            </Button>
<br/><br/><br/>
            {/* Offre de Lancement - Compacte */}
            <Link to="/pass-codeur" className="block max-w-4xl mx-auto">
              <div className="bg-gradient-to-r from-red-500 to-orange-500 rounded-2xl p-6 cursor-pointer hover:scale-105 transition-all transform hover:shadow-2xl">
                <div className="text-center">
                  <p className="text-xl sm:text-2xl font-bold text-white mb-2">
                    🔥 <span className="bg-yellow-300 text-black px-2 py-1 rounded">OFFRE DE LANCEMENT</span> Pass Codeur à <span className="line-through text-red-200">49,99€</span> → <span className="text-yellow-300 text-3xl font-black">0€</span> 🔥
                  </p>
                  <p className="text-lg text-yellow-100 font-semibold">
                    -100% de réduction • Durée limitée
                  </p>
                </div>
              </div>
            </Link>
            
          </div>
        </div>
      </section>

      {/* Philosophy Section - Tons chauds modernes */}
      <section className="text-foreground py-12">
        <div className="w-full py-16">
          <div className="max-w-5xl mx-auto text-center px-6">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6 sm:mb-8">Ma philosophie</h2>
            
            <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-3xl p-4 sm:p-8 mb-6 sm:mb-8 border border-border">
              <p className="text-xl sm:text-3xl font-bold mb-4 sm:mb-6 text-white">Un minimum de théorie, un maximum de pratique !</p>
              <p className="text-yellow-200 text-lg sm:text-2xl font-bold">Dès les premières minutes, tu produis un résultat concret et visible.</p>
            </div>
            
            <div className="space-y-8">
              <div className="bg-muted/30 backdrop-blur-sm rounded-2xl p-4 sm:p-10 border border-border text-left">
                <h3 className="text-2xl sm:text-4xl font-bold mb-6 sm:mb-10 flex items-center justify-center">
                  <CheckCircle className="w-8 sm:w-12 h-8 sm:h-12 text-green-500 mr-2 sm:mr-4" />
                  Aucun prérequis nécessaire
                </h3>
                <div className="grid md:grid-cols-1 gap-4 sm:gap-8 text-base sm:text-xl">
                  <div className="">
                    <p className="font-semibold">• Pas besoin de savoir coder ou d'être à l'aise en informatique.</p>
                  </div>
                  <div className="">
                    <p className="font-semibold">• Pas besoin d'installer des logiciels compliqués : tout se fait directement sur le site.</p>
                  </div>
                  <div className="">
                    <p className="font-semibold">• Pas de compilation, pas de configuration : ça marche tout de suite.</p>
                  </div>
                  <div className="">
                    <p className="font-semibold">• Apprentissage d'un langage simple (Javascript), couplé à un framework ultra léger (P5.js)</p>
                  </div>
                </div>
              </div>
          
              <div className="bg-gradient-to-r from-slate-200 to-slate-300 dark:from-slate-600 dark:to-slate-700 rounded-3xl p-4 sm:p-8 mb-6 sm:mb-8 border border-border">
                <p className="text-base sm:text-xl font-semibold text-slate-800 dark:text-white">
                  Ici, on ne se contente pas de recopier bêtement un tuto trouvé sur Internet.<br />
                  <span className="text-primary text-lg sm:text-2xl font-bold">Le but : comprendre la logique derrière les jeux vidéo.</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Courses Section - Tons frais et modernes */}
      <section className="bg-gradient-to-br from-cyan-400 via-blue-500 to-indigo-600 text-white py-12">
        <div className="w-full py-16">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-8 sm:mb-12">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6">Cours disponibles dès maintenant</h2>
              <p >Les cours utilisent un langage simple, le JavaScript, ainsi qu’un framework ultra-léger : P5.js.<br/>
              P5.js est une boîte à outils qui propose toutes les fonctions nécessaires à la création de jeux vidéo.</p>
            </div>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-8">
              <Card className="bg-white/15 backdrop-blur-sm border-white/20 text-white hover:bg-white/25 transition-all group">
                <CardHeader className="text-center p-4 sm:p-6">
                  <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-full p-4 sm:p-6 w-fit mx-auto mb-4 sm:mb-6 group-hover:scale-110 transition-transform">
                    <CodeIcon className="w-8 sm:w-12 h-8 sm:h-12 text-white" />
                  </div>
                  <CardTitle className="text-lg sm:text-2xl">Les bases de la programmation</CardTitle>
                  <CardDescription className="text-cyan-100 text-sm sm:text-lg">
                    Découvre les fondations du code.
                  </CardDescription>
                </CardHeader>
              </Card>
              
              <Card className="bg-white/15 backdrop-blur-sm border-white/20 text-white hover:bg-white/25 transition-all group">
                <CardHeader className="text-center p-4 sm:p-6">
                  <div className="bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full p-4 sm:p-6 w-fit mx-auto mb-4 sm:mb-6 group-hover:scale-110 transition-transform">
                    <TargetIcon className="w-8 sm:w-12 h-8 sm:h-12 text-white" />
                  </div>
                  <CardTitle className="text-lg sm:text-2xl">Jeu 1 : Améliore ta visée</CardTitle>
                  <CardDescription className="text-cyan-100 text-sm sm:text-lg">
                    Code un mini-jeu pour entraîner ta précision.
                  </CardDescription>
                </CardHeader>
              </Card>
              
              <Card className="bg-white/15 backdrop-blur-sm border-white/20 text-white hover:bg-white/25 transition-all group">
                <CardHeader className="text-center p-4 sm:p-6">
                  <div className="bg-gradient-to-r from-red-500 to-pink-500 rounded-full p-4 sm:p-6 w-fit mx-auto mb-4 sm:mb-6 group-hover:scale-110 transition-transform">
                    <TicTacIcon className="w-8 sm:w-12 h-8 sm:h-12 text-white" />
                  </div>
                  <CardTitle className="text-lg sm:text-2xl">Jeu 2 : Morpion</CardTitle>
                  <CardDescription className="text-cyan-100 text-sm sm:text-lg">
                    Le classique jeu du Tic-Tac-Toe.
                  </CardDescription>
                </CardHeader>
              </Card>
              
              <Card className="bg-white/15 backdrop-blur-sm border-white/20 text-white hover:bg-white/25 transition-all group">
                <CardHeader className="text-center p-4 sm:p-6">
                  <div className="bg-gradient-to-r from-green-500 to-emerald-500 rounded-full p-4 sm:p-6 w-fit mx-auto mb-4 sm:mb-6 group-hover:scale-110 transition-transform">
                    <Bird className="w-8 sm:w-12 h-8 sm:h-12 text-white" />
                  </div>
                  <CardTitle className="text-lg sm:text-2xl">Jeu 3 : Flappy Bird</CardTitle>
                  <CardDescription className="text-cyan-100 text-sm sm:text-lg">
                    Recrée le célèbre jeu du petit oiseau.
                  </CardDescription>
                </CardHeader>
              </Card>
              
              <Card className="bg-white/15 backdrop-blur-sm border-white/20 text-white hover:bg-white/25 transition-all group">
                <CardHeader className="text-center p-4 sm:p-6">
                  <div className="bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full p-4 sm:p-6 w-fit mx-auto mb-4 sm:mb-6 group-hover:scale-110 transition-transform">
                    <SnakeIcon className="w-8 sm:w-12 h-8 sm:h-12 text-white" />
                    </div>
                  <CardTitle className="text-lg sm:text-2xl">Jeu 4 : Snake</CardTitle>
                  <CardDescription className="text-cyan-100 text-sm sm:text-lg">
                    Programme le légendaire jeu du serpent.
                  </CardDescription>
                </CardHeader>
              </Card>
              
              <Link to="/courses">
                <Card className="bg-gradient-to-br from-yellow-400 via-orange-500 to-red-500 border-yellow-300 text-white hover:scale-105 transition-all group cursor-pointer">
                  <CardHeader className="text-center p-4 sm:p-6">
                    <div className="bg-white/20 rounded-full p-4 sm:p-6 w-fit mx-auto mb-4 sm:mb-6 group-hover:scale-110 transition-transform">
                      <ArrowRight className="w-8 sm:w-12 h-8 sm:h-12 text-white" />
                    </div>
                    <CardTitle className="text-lg sm:text-2xl">Et ce n'est que le début...</CardTitle>
                    <CardDescription className="text-yellow-100 text-sm sm:text-lg">
                      D'autres cours arriveront bientôt !
                    </CardDescription>
                  </CardHeader>
                </Card>
              </Link>
            </div>
          </div>
        </div>
      </section>

      
      
      {/* Community Section - Adapté aux thèmes light/dark */}
      <section className="text-foreground py-12">
        <div className="w-full py-16">
          <div className="max-w-5xl mx-auto text-center px-6">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6 sm:mb-8 flex items-center justify-center gap-2 sm:gap-4">
              Rejoins la communauté Discord
            </h2>

            
            <p className="text-lg sm:text-2xl mb-6 sm:mb-8 text-muted-foreground">
              Apprendre à coder, c'est encore plus simple quand on n'est pas seul !<br />
            </p>
            
                         {/* Bouton Discord */}
           <Button 
             size="lg" 
             className="bg-[#5865F2] text-white hover:bg-[#4752C4] text-lg sm:text-3xl px-8 sm:px-16 py-6 sm:py-10 rounded-full font-black shadow-2xl transform hover:scale-105 transition-all mt-1 mb-8 sm:mb-12"
             asChild
           >
             <Link to={EXTERNAL_LINKS.DISCORD} target="_blank" rel="noopener noreferrer">
               <DiscordIcon className="mr-2 sm:mr-4 w-6 sm:w-10 h-6 sm:h-10" /> Rejoindre Discord
             </Link>
           </Button>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-8">
              <div className="bg-muted/30 backdrop-blur-sm rounded-2xl p-4 sm:p-8 border border-border hover:bg-muted/50 transition-all">
                <div className="bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full p-4 sm:p-6 w-fit mx-auto mb-4 sm:mb-6">
                  <QuestionIcon className="w-8 sm:w-12 h-8 sm:h-12 text-white" />
                </div>
                <p className="text-base sm:text-xl font-semibold text-foreground">Pose tes questions et obtient de l'aide rapidement.</p>
              </div>
              
              <div className="bg-muted/30 backdrop-blur-sm rounded-2xl p-4 sm:p-8 border border-border hover:bg-muted/50 transition-all">
                <div className="bg-gradient-to-r from-green-500 to-emerald-500 rounded-full p-4 sm:p-6 w-fit mx-auto mb-4 sm:mb-6">
                  <Share2 className="w-8 sm:w-12 h-8 sm:h-12 text-white" />
                </div>
                <p className="text-base sm:text-xl font-semibold text-foreground">Partage tes créations avec les autres membres et obtient des retours constructifs.</p>
              </div>
              
              <div className="bg-muted/30 backdrop-blur-sm rounded-2xl p-4 sm:p-8 border border-border hover:bg-muted/50 transition-all sm:col-span-2 md:col-span-1">
                <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-full p-4 sm:p-6 w-fit mx-auto mb-4 sm:mb-6">
                  <Users className="w-8 sm:w-12 h-8 sm:h-12 text-white" />
                </div>
                <p className="text-base sm:text-xl font-semibold text-foreground">Discute avec d'autres passionnés de jeux vidéo et de programmation.</p>
              </div>
            </div>
            
          </div>
        </div>
      </section>

      {/* Pass Codeur Section - Utilise exactement les couleurs du CSS */}
      <section className="bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 text-white py-12">
        <div className="w-full py-16">
          <div className="max-w-5xl mx-auto text-center px-6">
            <div className="bg-white/20 backdrop-blur-sm rounded-full p-4 sm:p-6 w-fit mx-auto mb-6 sm:mb-8">
              <Crown className="w-12 sm:w-20 h-12 sm:h-20 text-white" />
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6 sm:mb-8">Un seul paiement pour tout débloquer</h2>
            <div className="bg-gradient-to-r from-yellow-400 to-orange-400 hover:from-yellow-500 hover:to-orange-500 text-black rounded-full px-8 sm:px-16 py-4 sm:py-6 mb-6 sm:mb-8 inline-block hover:scale-105 transition-all transform shadow-2xl">
                             <Link to="/pass-codeur">
                                   <button className="text-xl sm:text-3xl font-black bg-transparent border-none cursor-pointer">
                   Le Pass Codeur
                 </button>
               </Link>
            </div>
            
            <div className="bg-white/15 backdrop-blur-sm rounded-3xl p-4 sm:p-8 border border-white/20">
              <div className="grid sm:grid-cols-2 gap-4 sm:gap-8">
                <div className="space-y-4 sm:space-y-6">
                  <div className="flex items-start space-x-3 sm:space-x-4 bg-black/30 rounded-xl p-3 sm:p-4">
                    <CheckCircle className="w-6 sm:w-8 h-6 sm:h-8 text-yellow-300 mt-1 flex-shrink-0" />
                    <p className="text-base sm:text-xl font-semibold">Accès à tous les cours, actuels et futurs</p>
                  </div>
                  <div className="flex items-start space-x-3 sm:space-x-4 bg-black/30 rounded-xl p-3 sm:p-4">
                    <CheckCircle className="w-6 sm:w-8 h-6 sm:h-8 text-yellow-300 mt-1 flex-shrink-0" />
                    <p className="text-base sm:text-xl font-semibold">Un éditeur pour créer tes propres jeux</p>
                  </div>
                </div>
                <div className="space-y-4 sm:space-y-6">
                  <div className="flex items-start space-x-3 sm:space-x-4 bg-black/30 rounded-xl p-3 sm:p-4">
                    <CheckCircle className="w-6 sm:w-8 h-6 sm:h-8 text-yellow-300 mt-1 flex-shrink-0" />
                    <p className="text-base sm:text-xl font-semibold">Un espace pour montrer tes jeux</p>
                  </div>
                  <div className="flex items-start space-x-3 sm:space-x-4 bg-black/30 rounded-xl p-3 sm:p-4">
                    <CheckCircle className="w-6 sm:w-8 h-6 sm:h-8 text-yellow-300 mt-1 flex-shrink-0" />
                    <p className="text-base sm:text-xl font-semibold">Support via Discord</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Adapté aux thèmes light/dark */}
      <section className="text-foreground bg-muted/30 py-12">
        <div className="w-full py-20">
          <div className="max-w-5xl mx-auto text-center px-6">
            <div className="bg-primary/20 backdrop-blur-sm rounded-full p-4 sm:p-8 w-fit mx-auto mb-6 sm:mb-8">
              <Sparkles className="w-16 sm:w-24 h-16 sm:h-24 text-primary" />
            </div>
            <h2 className="text-3xl sm:text-5xl md:text-6xl font-bold mb-6 sm:mb-8">Prêt à créer ton premier jeu ?</h2>
            <div className="mb-8 sm:mb-12">
              <br/>
              <Button 
                size="lg" 
                className="bg-primary text-primary-foreground hover:bg-primary/90 text-lg sm:text-3xl px-8 sm:px-16 py-6 sm:py-10 rounded-full font-black shadow-2xl transform hover:scale-105 transition-all"
                asChild
              >
            <Link to="/courses">
                  👉 C'EST PARTI ! <ArrowRight className="ml-2 sm:ml-4 w-6 sm:w-10 h-6 sm:h-10" />
            </Link>
          </Button>
          
          
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MyFirstGame; 